import type { ReactNode } from 'react'
import { motion } from 'framer-motion'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'

interface HeroProps {
  pretitle?: string
  title: string
  subtitle?: string
  backgroundImage?: string
  backgroundVideo?: string
  overlay?: boolean
  centered?: boolean
  size?: 'sm' | 'md' | 'lg'
  showScrollButton?: boolean
  children?: ReactNode
}

const sizes = {
  sm: 'min-h-[40vh] pt-28 pb-16',
  md: 'min-h-[60vh] pt-32 pb-20',
  lg: 'min-h-screen pt-36 pb-24',
}

export function Hero({
  pretitle,
  title,
  subtitle,
  backgroundImage,
  backgroundVideo,
  overlay = true,
  centered = false,
  size = 'md',
  showScrollButton = false,
  children,
}: HeroProps) {
  const handleScrollDown = () => {
    window.scrollTo({
      top: window.innerHeight - 80,
      behavior: 'smooth',
    })
  }
  return (
    <section
      className={cn(
        'relative flex items-center overflow-hidden bg-black',
        sizes[size]
      )}
      style={{ marginTop: '-80px' }}
    >
      {/* Background Video */}
      {backgroundVideo && (
        <video
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 z-0 h-full w-full object-cover"
        >
          <source src={backgroundVideo} type="video/mp4" />
        </video>
      )}

      {/* Background Image */}
      {backgroundImage && !backgroundVideo && (
        <div
          className="absolute inset-0 z-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${backgroundImage})` }}
        />
      )}

      {/* Overlay */}
      {(backgroundImage || backgroundVideo) && overlay && (
        <div className="absolute inset-0 z-[1] bg-gradient-to-b from-primary-dark/70 via-primary/60 to-primary-dark/80" />
      )}

      {/* Content */}
      <div className={cn('container-main relative z-10', centered && 'text-center')}>
        <div className={cn(centered && 'mx-auto max-w-4xl')}>
          {pretitle && (
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              style={{
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                color: '#00a3cc',
                marginBottom: '0.75rem',
                textShadow: '0 1px 3px rgba(0,0,0,0.8), 0 3px 8px rgba(0,0,0,0.5), 0 6px 20px rgba(0,0,0,0.3)',
              }}
            >
              {pretitle}
            </motion.p>
          )}

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.05 }}
            className="font-heading text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl"
            style={{
              textShadow: '0 2px 4px rgba(0,0,0,0.8), 0 4px 12px rgba(0,0,0,0.6), 0 8px 24px rgba(0,0,0,0.4), 0 12px 48px rgba(0,0,0,0.2)',
            }}
          >
            {title}
          </motion.h1>

          {subtitle && (
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="mt-4 text-base text-white/90 sm:text-lg"
              style={{
                maxWidth: '600px',
                textShadow: '0 1px 3px rgba(0,0,0,0.8), 0 3px 8px rgba(0,0,0,0.5), 0 6px 20px rgba(0,0,0,0.3)',
              }}
            >
              {subtitle}
            </motion.p>
          )}

          {children && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="mt-6"
              style={{
                filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.4))',
              }}
            >
              {children}
            </motion.div>
          )}
        </div>
      </div>

      {/* Scroll Down Button */}
      {showScrollButton && (
        <motion.button
          onClick={handleScrollDown}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2 cursor-pointer"
          aria-label="Scroll down"
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
            style={{
              width: '44px',
              height: '44px',
              borderRadius: '50%',
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              backdropFilter: 'blur(8px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid rgba(255, 255, 255, 0.3)',
            }}
          >
            <ChevronDown className="w-6 h-6 text-white" />
          </motion.div>
        </motion.button>
      )}
    </section>
  )
}
